<script lang="ts">
import { Button } from "@pre6ai/lens-ui/ui/button";
import ArrowRight from "lucide-svelte/icons/arrow-right";
import type { Snippet } from "svelte";

/**
 * The /enterprise first screen, built on the homepage hero's layout: headline (ending in the red stop),
 * lede and call to action, with the hero graphic (its children) stacked underneath. `accent` is the
 * closing phrase of the title, set in brand red with a highlighter stroke. `eyebrow` is a short label
 * above the title. `ledeContent` is the lede, so it can carry markup and motion.
 */
let {
	title,
	accent,
	eyebrow,
	ledeContent,
	cta = { label: "Talk to us", href: "/#get-in-touch" },
	children,
}: {
	title: string;
	accent?: string;
	eyebrow?: string;
	ledeContent: Snippet;
	cta?: { label: string; href: string };
	children?: Snippet;
} = $props();

const lead = $derived(accent && title.endsWith(accent) ? title.slice(0, -accent.length) : title);
</script>

<div class="hero-root">
	<section class="hero" data-drafting="hero">
		<div class="intro">
			<div class="intro-main">
				{#if eyebrow}<p class="eyebrow">{eyebrow}</p>{/if}
				<h1>
					{lead}{#if lead !== title}<span class="accent">{accent}</span>{/if}<span class="stop">.</span>
				</h1>
			</div>
			<div class="intro-side">
				<p class="lede">{@render ledeContent()}</p>
				<Button href={cta.href} size="lg" class="cta">
					{cta.label} <ArrowRight size={16} strokeWidth={2.5} />
				</Button>
			</div>
		</div>
		{#if children}
			<div class="stage">{@render children()}</div>
		{/if}
	</section>
</div>

<style>
	/* Together with the navbar above it, the hero fills the first screen. */
	.hero-root {
		min-height: calc(100svh - var(--nav-h));
		display: flex;
		flex-direction: column;
		color: var(--foreground);
	}

	.hero {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: clamp(36px, 6vh, 64px);
		width: 100%;
		max-width: var(--page-max);
		margin: 0 auto;
		padding: 12px 32px 32px;
	}

	.stage {
		display: flex;
		flex-direction: column;
		gap: 12px;
		min-width: 0;
	}

	.intro {
		display: grid;
		grid-template-columns: minmax(0, 8fr) minmax(0, 4fr);
		align-items: end;
		gap: 56px;
	}

	.intro-side {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 20px;
		padding-bottom: 6px;
	}

	h1 {
		color: var(--ink);
		font-family: "Inter Variable", var(--font-sans);
		font-variation-settings: "opsz" 32;
		font-size: clamp(2.6rem, 4.6vw, 4.6rem);
		line-height: 1.06;
		font-weight: 600;
		letter-spacing: -0.025em;
		text-wrap: balance;
	}

	.stop,
	.accent {
		color: var(--primary);
	}

	/* The accent phrase gets a highlighter stroke across its lower half, swept in from the left once the
	   title has landed. It carries across a line break. */
	.accent {
		padding: 0 0.06em;
		background: linear-gradient(color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--primary) 16%, transparent))
			no-repeat 0 88% / 0% 42%;
		-webkit-box-decoration-break: clone;
		box-decoration-break: clone;
		animation: highlight 0.9s cubic-bezier(0.65, 0, 0.35, 1) 0.5s forwards;
	}

	@keyframes highlight {
		to {
			background-size: 100% 42%;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.accent {
			background-size: 100% 42%;
			animation: none;
		}
	}

	.eyebrow {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 18px;
		color: var(--primary);
		font: 500 12px/1 var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.eyebrow::before {
		content: "";
		width: 7px;
		height: 7px;
		border-radius: 2px;
		background: var(--primary);
	}

	.lede {
		max-width: 26rem;
		color: oklch(from var(--muted-foreground) calc(l - 0.05) c h);
		font-size: 17px;
		line-height: 26px;
		text-wrap: pretty;
	}

	.intro-side :global(.cta) {
		height: 44px;
		padding-inline: 18px;
		font-size: 15px;
		font-weight: 600;
	}

	@media (max-width: 1023px) {
		.intro {
			grid-template-columns: minmax(0, 1fr);
			gap: 24px;
		}
	}

	@media (max-width: 767px) {
		.hero {
			padding: 16px 16px 32px;
		}
	}
</style>
